(() => {
  const ROOT_SELECTOR = 'product-content.product--page';
  const MEDIA_IMAGE_SELECTOR = 'img[data-product-active-media-image]';
  const CANDIDATE_SELECTOR = 'img[data-product-active-media-lcp-candidate]';
  const PROMOTED_ATTRIBUTE = 'data-product-active-media-lcp-promoted';
  const PATCHED_ATTRIBUTE = 'data-product-active-media-lcp-preserve-patched';
  const LEGACY_QUERY_VALUE = 'legacy';
  const roots = new Set();
  const pendingRoots = new Set();
  let scheduledFrame = null;
  let isDesktopViewport = window.matchMedia('(min-width: 768px)').matches;

  // ProductContent 会按 variant/A/B 克隆 slide；保留匹配候选的原始节点，维持 parser discovery。
  const preserveInitialCandidate = (root) => {
    if (!root || root.hasAttribute(PATCHED_ATTRIBUTE) || typeof root.replaceCarouselSlides !== 'function') return;

    root.setAttribute(PATCHED_ATTRIBUTE, 'true');
    root.replaceCarouselSlides = function replaceCarouselSlidesWithInitialCandidate(container) {
      const existingCandidateSlide = [...container.children].find((slide) => (
        slide.querySelector(CANDIDATE_SELECTOR)
      ));
      const existingCandidateId = existingCandidateSlide?.dataset.mediaId;
      const carouselImages = this.getImages(this.currentImages.carouselImages);

      if (existingCandidateId) {
        const candidateIndex = carouselImages.findIndex((slide) => (
          slide.dataset.mediaId === existingCandidateId && slide.querySelector(CANDIDATE_SELECTOR)
        ));
        if (candidateIndex >= 0) carouselImages[candidateIndex] = existingCandidateSlide;
      }

      carouselImages[0].classList.add('is-selected');
      carouselImages[0].classList.toggle('product-media-carousel-mobile__slide--alone', carouselImages.length < 2);
      this.pasteImages(container, carouselImages);
    };
  };

  const registerRoot = (root) => {
    if (!root || !root.matches(ROOT_SELECTOR)) return null;
    roots.add(root);
    preserveInitialCandidate(root);
    return root;
  };

  const registerRoots = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) registerRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(registerRoot);
  };

  const pruneRoots = () => {
    roots.forEach((root) => {
      if (!root.isConnected) roots.delete(root);
    });
  };

  const getEventRoot = (event) => {
    const target = event.target;
    return target instanceof Element ? registerRoot(target.closest(ROOT_SELECTOR)) : null;
  };

  // A/B、Desktop/Mobile 和 Quick View 会同时存在 DOM；只认可真正占据布局的主区域。
  const isVisible = (element) => {
    if (!element || !element.isConnected || element.getClientRects().length === 0) return false;

    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  };

  const activateResponsiveSource = (image) => {
    if (image.dataset.srcset && !image.getAttribute('srcset')) {
      image.setAttribute('srcset', image.dataset.srcset);
    }
    if (!image.getAttribute('sizes') || image.getAttribute('sizes') === 'auto') {
      image.setAttribute('sizes', isDesktopViewport ? '50vw' : '100vw');
    }

    const intrinsicWidth = Number.parseInt(image.dataset.intrinsicWidth || '', 10);
    const intrinsicHeight = Number.parseInt(image.dataset.intrinsicHeight || '', 10);
    if (intrinsicWidth > 0 && intrinsicHeight > 0) {
      const width = String(intrinsicWidth);
      const height = String(intrinsicHeight);
      if (image.getAttribute('width') !== width) image.setAttribute('width', width);
      if (image.getAttribute('height') !== height) image.setAttribute('height', height);
    }
  };

  const demote = (image) => {
    if (image.hasAttribute(PROMOTED_ATTRIBUTE)) image.removeAttribute(PROMOTED_ATTRIBUTE);
    if (image.getAttribute('fetchpriority') !== 'low') image.setAttribute('fetchpriority', 'low');
    if (image.getAttribute('loading') !== 'lazy') image.setAttribute('loading', 'lazy');
  };

  const findActiveImage = (root) => {
    const visibleContainers = [...root.querySelectorAll('[data-product-desktop-media], [data-product-mobile-media]')]
      .filter(isVisible);

    for (const container of visibleContainers) {
      const carousel = [...container.querySelectorAll('product-media-carousel')].find(isVisible);
      if (carousel) {
        const selectedSlide = carousel.querySelector('.carousel__slide.is-selected[data-media-id]');
        const selectedImage = selectedSlide?.querySelector(MEDIA_IMAGE_SELECTOR);
        if (selectedImage && isVisible(selectedImage)) return selectedImage;

        const initialCandidate = [...carousel.querySelectorAll(CANDIDATE_SELECTOR)].find(isVisible);
        if (initialCandidate) return initialCandidate;
      }

      const grid = [...container.querySelectorAll('product-media[data-container="grid"]')].find(isVisible);
      if (grid) {
        const selectedGridImage = [...grid.querySelectorAll(`.product-media-grid__item ${MEDIA_IMAGE_SELECTOR}`)]
          .find(isVisible);
        if (selectedGridImage) return selectedGridImage;
      }
    }

    return null;
  };

  const updateActiveMedia = (root) => {
    if (!root?.isConnected) return;

    const activeImage = findActiveImage(root);
    root.querySelectorAll(MEDIA_IMAGE_SELECTOR).forEach((image) => {
      if (image !== activeImage) demote(image);
    });

    if (!activeImage) return;
    activateResponsiveSource(activeImage);
    if (activeImage.getAttribute('loading') !== 'eager') activeImage.setAttribute('loading', 'eager');
    if (activeImage.getAttribute('fetchpriority') !== 'high') activeImage.setAttribute('fetchpriority', 'high');
    if (!activeImage.hasAttribute(PROMOTED_ATTRIBUTE)) activeImage.setAttribute(PROMOTED_ATTRIBUTE, 'true');
  };

  const flushUpdates = () => {
    scheduledFrame = null;
    pruneRoots();
    const currentRoots = [...pendingRoots];
    pendingRoots.clear();
    currentRoots.forEach(updateActiveMedia);
  };

  const scheduleUpdate = (root = null) => {
    if (root) {
      pendingRoots.add(root);
    } else {
      pruneRoots();
      roots.forEach((item) => pendingRoots.add(item));
    }
    if (scheduledFrame !== null) return;
    scheduledFrame = window.requestAnimationFrame(flushUpdates);
  };

  const handleScopedMediaEvent = (event) => {
    scheduleUpdate(getEventRoot(event));
  };

  const initialize = () => {
    registerRoots();
    scheduleUpdate();
  };

  document.addEventListener('DOMContentLoaded', initialize, { once: true });
  document.addEventListener('pdp-media-ready', handleScopedMediaEvent);
  document.addEventListener('pdp-media-experiment-change', () => {
    registerRoots();
    scheduleUpdate();
  });
  document.addEventListener('carouselSelect', handleScopedMediaEvent);
  document.addEventListener('change', (event) => {
    const variantPicker = event.target.closest?.(`${ROOT_SELECTOR} variant-picker`);
    if (variantPicker) scheduleUpdate(registerRoot(variantPicker.closest(ROOT_SELECTOR)));
  });

  document.addEventListener('shopify:section:load', (event) => {
    registerRoots(event.target);
    scheduleUpdate(getEventRoot(event));
  });
  document.addEventListener('shopify:section:reorder', () => {
    registerRoots();
    scheduleUpdate();
  });

  window.addEventListener('resize', () => {
    const nextIsDesktopViewport = window.matchMedia('(min-width: 768px)').matches;
    if (nextIsDesktopViewport === isDesktopViewport) return;
    isDesktopViewport = nextIsDesktopViewport;
    scheduleUpdate();
  }, { passive: true });

  const installLegacyObserver = () => {
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === 'childList'
        || mutation.attributeName === 'class')) {
        registerRoots();
        scheduleUpdate();
      }
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  };

  if (new URLSearchParams(window.location.search).get('vl_product_lcp_observer') === LEGACY_QUERY_VALUE) {
    installLegacyObserver();
  }

  customElements.whenDefined('product-content').then(() => {
    registerRoots();
    scheduleUpdate();
  });
  initialize();
})();
